import * as Yup from 'yup'
import dayjs from 'dayjs'
import { CardBrand, CardBrandInfo, CardBrandPattern } from './BankPaymentForm.types'

const CARD_BRANDS: CardBrandPattern[] = [
  {
    type: 'visa',
    niceType: 'Visa',
    prefixPattern: /^4/,
    fullPattern: /^4\d{12}(?:\d{3})?$/,
  },
  {
    type: 'mastercard',
    niceType: 'Mastercard',
    prefixPattern: /^(?:5[1-5]|2(?:2[2-9]\d|[3-6]\d{2}|7[01]\d|720))/,
    fullPattern: /^(?:5[1-5]\d{14}|2(?:2[2-9]\d|[3-6]\d{2}|7[01]\d|720)\d{12})$/,
  },
  {
    type: 'amex',
    niceType: 'American Express',
    prefixPattern: /^3[47]/,
    fullPattern: /^3[47]\d{13}$/,
  },
  {
    type: 'discover',
    niceType: 'Discover',
    prefixPattern: /^6(?:011|5|4[4-9])/,
    fullPattern: /^6(?:011|5\d{2}|4[4-9]\d)\d{12}$/,
  },
  {
    type: 'jcb',
    niceType: 'JCB',
    prefixPattern: /^(?:2131|1800|35)/,
    fullPattern: /^(?:2131|1800|35\d{3})\d{11}$/,
  },
  {
    type: 'diners',
    niceType: 'Diners Club',
    prefixPattern: /^3(?:0[0-5]|[68])/,
    fullPattern: /^3(?:0[0-5]|[68]\d)\d{11}$/,
  },
  {
    type: 'unionpay',
    niceType: 'UnionPay',
    prefixPattern: /^62/,
    fullPattern: /^62\d{14,17}$/,
  },
  {
    type: 'maestro',
    niceType: 'Maestro',
    prefixPattern: /^(?:50|5[6-9]|6[0-9])/,
    fullPattern: /^(?:50|5[6-9]|6[0-9])\d{10,17}$/,
  },
]

const ELIGIBLE_CARD_BRANDS: CardBrand[] = ['visa', 'mastercard']

export const getCardBrand = (cardNumber: string): CardBrandInfo => {
  const clean = cardNumber.replace(/\s+/g, '')

  if (!clean || !/^\d+$/.test(clean)) {
    return { type: 'unknown', niceType: 'Unknown' }
  }

  const match = CARD_BRANDS.find((brand) => brand.prefixPattern.test(clean))

  if (match) {
    return { type: match.type, niceType: match.niceType }
  }

  return { type: 'unknown', niceType: 'Unknown' }
}

const isValidLuhn = (num: string): boolean => {
  let sum = 0
  let doubleIt = false

  for (let i = num.length - 1; i >= 0; i--) {
    let digit = num.charCodeAt(i) - 48

    if (digit < 0 || digit > 9) {
      return false
    }

    if (doubleIt) {
      digit *= 2

      if (digit > 9) {
        digit -= 9
      }
    }

    sum += digit
    doubleIt = !doubleIt
  }

  return sum % 10 === 0
}

const isEligibleCardNumber = (value: string): boolean => {
  const cleanNumber = value.replace(/\s+/g, '')

  return CARD_BRANDS.filter((brand) => ELIGIBLE_CARD_BRANDS.includes(brand.type)).some((brand) =>
    brand.fullPattern.test(cleanNumber),
  )
}

const isValidRoutingNumber = (routingNumber: string): boolean => {
  if (!/^\d{9}$/.test(routingNumber)) {
    return false
  }

  const digits = routingNumber.split('').map(Number)
  const checksum =
    3 * (digits[0] + digits[3] + digits[6]) +
    7 * (digits[1] + digits[4] + digits[7]) +
    1 * (digits[2] + digits[5] + digits[8])

  return checksum % 10 === 0
}

const isValidAccountNumber = (accountNumber: string): boolean => {
  return /^\d{6,17}$/.test(accountNumber)
}

const isValidZipCode = (zipCode: string): boolean => {
  return /^\d{5}$/.test(zipCode)
}

Yup.addMethod(Yup.string, 'validateCreditCardNumber', function (errorMessage) {
  return this.test('test-credit-card-number', errorMessage, function (value) {
    const { path, createError } = this

    if (!value) {
      return true
    }

    const cleanValue = value.replace(/\s+/g, '')

    if (!/^\d{13,19}$/.test(cleanValue)) {
      return createError({ path, message: errorMessage })
    }

    const brand = getCardBrand(value)

    if (!ELIGIBLE_CARD_BRANDS.includes(brand.type)) {
      const brandLabel = brand.type !== 'unknown' ? brand.niceType : 'This card'

      return createError({
        path,
        message: `${brandLabel} is not eligible as a settlement method. Please use Visa or Mastercard debit card.`,
      })
    }

    if (!isEligibleCardNumber(value) || !isValidLuhn(cleanValue)) {
      return createError({ path, message: errorMessage })
    }

    return true
  })
})

Yup.addMethod(Yup.string, 'validateRoutingNumber', function (errorMessage) {
  return this.test('test-routing-number', errorMessage, function (value) {
    const { path, createError } = this

    if (!value) {
      return true
    }

    if (!isValidRoutingNumber(value)) {
      return createError({ path, message: errorMessage })
    }

    return true
  })
})

Yup.addMethod(Yup.string, 'validateAccountNumber', function (errorMessage) {
  return this.test('test-account-number', errorMessage, function (value) {
    const { path, createError } = this

    if (!value) {
      return true
    }

    if (!isValidAccountNumber(value)) {
      return createError({ path, message: errorMessage })
    }

    return true
  })
})

Yup.addMethod(Yup.string, 'validateZipCode', function (errorMessage) {
  return this.test('test-zip-code', errorMessage, function (value) {
    const { path, createError } = this

    if (!value) {
      return true
    }

    if (!isValidZipCode(value)) {
      return createError({ path, message: errorMessage })
    }

    return true
  })
})

Yup.addMethod(Yup.string, 'validateExpirationDate', function (errorMessage) {
  return this.test(`test-expiration-date`, errorMessage, function (value) {
    const { path, createError } = this

    if (!value) {
      return createError({ path, message: errorMessage })
    }

    const cleanValue = value.replace(/undefined/g, '').replace(/--/g, '-')

    const date = dayjs(cleanValue, 'MM-YYYY', true)

    if (!date.isValid()) {
      return createError({ path, message: 'Date must be in MM-YYYY format' })
    }

    if (date.isBefore(dayjs(), 'month')) {
      return createError({ path, message: 'Expiration date cannot be in the past' })
    }

    return true
  })
})

export const DebitCardSchema = Yup.object().shape({
  nameOnCard: Yup.string().required('Name on card is required'),
  cardNumber: Yup.string()
    .required('Card number is required')
    // @ts-ignore
    .validateCreditCardNumber('Invalid card number'),
  expirationDate: Yup.string()
    .required('Expiration date is required')
    // @ts-ignore
    .validateExpirationDate('Expiration date is invalid'),
  zipCode: Yup.string()
    .required('Zip code is required')
    // @ts-ignore
    .validateZipCode('Postal code must be 5 digits'),
})

export const BankInformationSchema = Yup.object().shape({
  routingNumber: Yup.string()
    .required('Routing number is required')
    // @ts-ignore
    .validateRoutingNumber('Invalid routing number'),
  accountNumber: Yup.string()
    .required('Account number is required')
    // @ts-ignore
    .validateAccountNumber('Account number must be 6-17 digits'),
  accountType: Yup.string().required('Account type is required'),
})
